import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { TriggeredQuestionnaireMeta } from '@/lib/questionnaire-triggers/types';
import { Clock, FileQuestion } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TriggeredQuestionnaireCardProps {
  meta: TriggeredQuestionnaireMeta;
  className?: string;
  children?: React.ReactNode;
}

export function TriggeredQuestionnaireCard({
  meta,
  className,
  children,
}: TriggeredQuestionnaireCardProps) {
  return (
    <Card
      className={cn(
        'rounded-xl border-l-4 border-l-primary border border-border/80 bg-primary/5 shadow-sm',
        className
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold tracking-tight">{meta.title}</h3>
        </div>
        {meta.description ? (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {meta.description}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          {meta.questionCount > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <FileQuestion className="h-3.5 w-3.5" />
              {meta.questionCount} question{meta.questionCount !== 1 ? 's' : ''}
            </span>
          ) : null}
          {meta.estimatedTimeMinutes > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              ~{meta.estimatedTimeMinutes} min
            </span>
          ) : null}
        </div>
      </CardHeader>
      {children ? (
        <CardContent className="pt-0 space-y-6">
          {children}
        </CardContent>
      ) : null}
    </Card>
  );
}
